import { generateId } from '@freelancer/datastore/testing';
import {
  FolderApi,
  PrivacyLevelApi,
  ThreadTypeApi,
} from 'api-typings/messages/messages_types';
import {
  generateMessage,
  GenerateMessageOptions,
} from '../messages/message.seed';
import {
  Thread,
  ThreadContext,
  ThreadContextType,
  ThreadType,
} from './threads.model';

export interface GenerateThreadsOptions {
  readonly id?: number;
  readonly userId: number;
  readonly otherMembers: readonly number[];
  readonly context: ThreadContext;
  readonly unreadMessageCount?: number;
  readonly message?: Omit<GenerateMessageOptions, 'threadId'>;
  readonly threadType?: ThreadType;
  readonly folder?: FolderApi;
  readonly isMuted?: boolean;
  readonly isBlocked?: boolean;
  readonly writePrivacy?: PrivacyLevelApi;
  readonly inactiveMembers?: readonly number[];
}

export function generateThread({
  id = generateId(),
  userId,
  otherMembers,
  context,
  unreadMessageCount = 0,
  message,
  threadType = ThreadTypeApi.PRIVATE_CHAT,
  folder = FolderApi.INBOX,
  isMuted = false,
  isBlocked = false,
  writePrivacy = PrivacyLevelApi.MEMBERS,
  inactiveMembers = [],
}: GenerateThreadsOptions): Thread {
  return {
    context,
    contextType: context.type,
    folder,
    id,
    isBlocked,
    isFake: false,
    isMuted,
    isRead: unreadMessageCount === 0,
    members: [...otherMembers, userId],
    inactiveMembers,
    message: message
      ? generateMessage({ ...message, threadId: id })
      : undefined,
    messageUnreadCount: unreadMessageCount,
    otherMembers,
    owner: userId,
    threadType:
      otherMembers.length > 1
        ? // FIXME: This needs to take into account the various Team-related thread types
          // and context types as described in T36092
          ThreadTypeApi.GROUP
        : threadType ?? ThreadTypeApi.PRIVATE_CHAT,
    timeCreated: Date.now(),
    timeRead: Date.now(),
    timeUpdated: Date.now(),
    userReadTimes: {
      [userId]: Date.now(),
      [otherMembers[0]]: Date.now(),
    },
    writePrivacy,
  };
}

// --- Mixins ---
export function withContext(
  id: number,
  type: ThreadContextType,
): Pick<GenerateThreadsOptions, 'context'> {
  return {
    context: {
      id,
      type,
    },
  };
}

export function withMessage(
  message: string,
  fromUserId: number,
): Pick<GenerateThreadsOptions, 'message'> {
  return {
    message: {
      attachments: [],
      fromUserId,
      message,
    },
  };
}
